import type { Countdown, Note } from '@just-us/shared'
/**
 * pages/home.tsx
 *
 * Home screen — Days Counter Header + bottom nav + quick-access sections.
 * Requires authenticated user with a couple_id.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BottomNav from '../components/bottom-nav'
import { useCountdowns } from '../hooks/use-countdowns'
import { useCouple } from '../hooks/use-couple'
import { useNotes } from '../hooks/use-notes'
import { usePush } from '../hooks/use-push'
import { useSync } from '../hooks/use-sync'
import { useWishlist } from '../hooks/use-wishlist'
import { getAuthPayload } from '../store/auth'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const daysBetween = (from: string): number => {
  const start = new Date(from)
  const now = new Date()
  start.setHours(0, 0, 0, 0)
  now.setHours(0, 0, 0, 0)
  return Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
}

const daysUntilNextAnniversary = (from: string): number => {
  const start = new Date(from)
  const now = new Date()
  const thisYear = now.getFullYear()
  let next = new Date(thisYear, start.getMonth(), start.getDate())
  if (next <= now) next = new Date(thisYear + 1, start.getMonth(), start.getDate())
  next.setHours(0, 0, 0, 0)
  now.setHours(0, 0, 0, 0)
  return Math.floor((next.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

const progressToAnniversary = (from: string): number => {
  const start = new Date(from)
  const now = new Date()
  const thisYear = now.getFullYear()
  let prev = new Date(thisYear, start.getMonth(), start.getDate())
  if (prev > now) prev = new Date(thisYear - 1, start.getMonth(), start.getDate())
  let next = new Date(thisYear, start.getMonth(), start.getDate())
  if (next <= now) next = new Date(thisYear + 1, start.getMonth(), start.getDate())
  const total = next.getTime() - prev.getTime()
  const elapsed = now.getTime() - prev.getTime()
  return Math.min(100, Math.round((elapsed / total) * 100))
}

const formatAnniversaryDate = (dateStr: string): string => {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
  isPast: boolean
}

const getTimeLeft = (targetDate: string): TimeLeft => {
  const target = new Date(targetDate + 'T00:00:00').getTime()
  const now = Date.now()
  const diff = target - now
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true }
  const totalSec = Math.floor(diff / 1000)
  const days = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const seconds = totalSec % 60
  return { days, hours, minutes, seconds, isPast: false }
}

const pad2 = (n: number) => n.toString().padStart(2, '0')

// ---------------------------------------------------------------------------
// Days Counter Header
// ---------------------------------------------------------------------------

interface DaysHeaderProps {
  daysTogether: number
  anniversaryDate: string
  progressPercent: number
  daysToAnniversary: number
  userAvatar: string
  partnerAvatar: string
  userNickname: string
  partnerNickname: string
  onSettings: () => void
}

const DaysHeader = ({
  daysTogether,
  anniversaryDate,
  progressPercent,
  daysToAnniversary,
  userAvatar,
  partnerAvatar,
  userNickname,
  partnerNickname,
  onSettings,
}: DaysHeaderProps) => (
  <div className="text-center px-5 pt-3 pb-4 relative">
    {/* Settings gear */}
    <button
      type="button"
      onClick={onSettings}
      aria-label="Settings"
      className="absolute top-3 right-5 w-[36px] h-[36px] rounded-full bg-white border border-[#F4C0D1] flex items-center justify-center text-[18px] active:scale-90 transition-all duration-150 touch-manipulation"
    >
      ⚙️
    </button>

    {/* Avatar pair */}
    <div className="flex items-center justify-center gap-3 mb-1">
      <div className="w-[44px] h-[44px] rounded-full bg-[#FBEAF0] border-2 border-[#F4C0D1] flex items-center justify-center text-[22px]">
        {userAvatar}
      </div>
      <span className="text-[16px]">💕</span>
      <div className="w-[44px] h-[44px] rounded-full bg-[#FBEAF0] border-2 border-[#F4C0D1] flex items-center justify-center text-[22px]">
        {partnerAvatar}
      </div>
    </div>
    <p className="font-['DM_Sans'] text-[12px] text-[#B89AA2]">
      {userNickname} 💕 {partnerNickname}
    </p>

    {/* Day counter */}
    <span className="font-['Playfair_Display'] text-[40px] font-semibold text-[#D4537E] leading-none block">
      Day {daysTogether}
    </span>
    <p className="font-['DM_Sans'] text-[12px] text-[#B89AA2] mt-1">
      together since {anniversaryDate}
    </p>

    {/* Anniversary progress bar */}
    <div className="mt-2 h-[3px] rounded-full bg-[#F4C0D1] overflow-hidden">
      <div
        className="h-full rounded-full bg-[#D4537E] transition-all duration-700"
        style={{ width: `${progressPercent}%` }}
      />
    </div>
    <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2] mt-1 text-right">
      Next anniversary in {daysToAnniversary} days
    </p>
  </div>
)

// ---------------------------------------------------------------------------
// Note preview card
// ---------------------------------------------------------------------------

const NoteCard = ({ note, onPress }: { note: Note; onPress: () => void }) => {
  const isLocked = !!(note.unlock_at && Math.floor(Date.now() / 1000) < note.unlock_at)

  return (
    <button
      type="button"
      onClick={onPress}
      className={[
        'w-full text-left rounded-[14px] border px-4 py-3 transition-all duration-150 touch-manipulation',
        isLocked
          ? 'bg-[#F9E0E8] border-[#F4C0D1] opacity-70'
          : 'bg-white border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] active:scale-[0.98]',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <span className="text-[20px] leading-none mt-0.5">
          {isLocked
            ? '🔒'
            : note.envelope_style === 'heart'
              ? '💌'
              : note.envelope_style === 'flower'
                ? '🌸'
                : '✉️'}
        </span>
        <div className="flex-1 min-w-0">
          {isLocked ? (
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] italic">
              Time-locked · tap to see when it opens
            </p>
          ) : note.opened_at ? (
            <p className="font-['Caveat'] text-[18px] text-[#2C1810] leading-snug line-clamp-2">
              {note.content ?? '(doodle)'}
            </p>
          ) : (
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] italic">
              You have a new note · tap to open
            </p>
          )}
          <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2] mt-1">
            {isLocked ? 'Locked' : note.opened_at ? 'Read' : 'Unread'} ·{' '}
            {new Date(note.created_at * 1000).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })}
          </p>
        </div>
        {!note.opened_at && !isLocked && (
          <div className="w-[8px] h-[8px] rounded-full bg-[#D4537E] mt-1 flex-shrink-0" />
        )}
      </div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Countdown preview card
// ---------------------------------------------------------------------------

const CountdownCard = ({ countdown, onPress }: { countdown: Countdown; onPress: () => void }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => getTimeLeft(countdown.target_date))

  useEffect(() => {
    if (timeLeft.isPast) return
    const timer = setInterval(() => {
      setTimeLeft(getTimeLeft(countdown.target_date))
    }, 1000)
    return () => clearInterval(timer)
  }, [countdown.target_date, timeLeft.isPast])

  return (
    <button
      type="button"
      onClick={onPress}
      className="w-full text-left bg-white rounded-[14px] border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] px-4 py-3 active:scale-[0.98] transition-all duration-150 touch-manipulation"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="font-['DM_Sans'] text-[14px] font-medium text-[#2C1810]">
            {countdown.title}
          </p>
          <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2] mt-0.5">
            {new Date(countdown.target_date).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="font-['Playfair_Display'] text-[28px] font-semibold text-[#D4537E] leading-none">
            {timeLeft.isPast ? (
              '🎉'
            ) : (
              <>
                {timeLeft.days > 0 ? `${timeLeft.days} ` : ''}
                <span className="font-['DM_Sans'] text-[14px] font-medium text-[#B89AA2]">
                  {pad2(timeLeft.hours)}:{pad2(timeLeft.minutes)}:{pad2(timeLeft.seconds)}
                </span>
              </>
            )}
          </span>
          {!timeLeft.isPast && timeLeft.days > 0 && (
            <span className="font-['DM_Sans'] text-[13px] text-[#B89AA2]">days</span>
          )}
        </div>
      </div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

const SkeletonCard = () => <div className="h-[72px] rounded-[14px] animate-shimmer" />

// ---------------------------------------------------------------------------
// Empty State Card
// ---------------------------------------------------------------------------

const EmptyState = ({
  icon,
  title,
  subtitle,
}: {
  icon: string
  title: string
  subtitle: string
}) => (
  <div className="bg-white rounded-[16px] border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] p-6 text-center">
    <div className="text-[36px] mb-3">{icon}</div>
    <p className="font-['Playfair_Display'] text-[17px] font-medium text-[#2C1810] mb-1">{title}</p>
    <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2]">{subtitle}</p>
  </div>
)

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function HomePage() {
  const { data, isLoading } = useCouple()
  const payload = getAuthPayload()
  const navigate = useNavigate()

  // Background hooks — run passively
  const { subscribeToPush } = usePush(payload?.user_id)
  useSync()

  // Data hooks
  const { data: inboxData, isLoading: notesLoading } = useNotes('inbox')
  const { data: countdownsData, isLoading: countdownsLoading } = useCountdowns()
  const { data: wishlistData } = useWishlist()

  // Request push permission once on mount (best-effort)
  useEffect(() => {
    subscribeToPush()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const couple = data?.data?.couple
  const users = data?.data?.users ?? []
  const currentUser = users.find((u) => u.id === payload?.user_id)
  const partner = users.find((u) => u.id !== payload?.user_id)

  const hasAnniversary = !!couple?.anniversary_date

  const daysTogether = hasAnniversary ? daysBetween(couple!.anniversary_date!) : 0
  const progress = hasAnniversary ? progressToAnniversary(couple!.anniversary_date!) : 0
  const daysToNext = hasAnniversary ? daysUntilNextAnniversary(couple!.anniversary_date!) : 365
  const formattedDate = hasAnniversary
    ? formatAnniversaryDate(couple!.anniversary_date!)
    : 'the beginning'

  // Latest 2 inbox notes (unread notes show "You have a new note" placeholder)
  const recentNotes = (inboxData?.data ?? []).slice(0, 2)

  // Next upcoming countdown (first one not yet passed)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const nextCountdown = (countdownsData?.data ?? []).find((c) => new Date(c.target_date) >= today)

  // Last 2 pending wishlist items (most recently added)
  const partnerNickname = partner?.nickname ?? 'your partner'
  const recentWishlistItems = (wishlistData?.data?.items ?? []).filter((i) => !i.doneAt).slice(0, 2)

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)]">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-8">
            <div className="text-[32px] animate-float">💕</div>
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2]">Loading…</p>
          </div>
        ) : (
          <DaysHeader
            daysTogether={daysTogether}
            anniversaryDate={formattedDate}
            progressPercent={progress}
            daysToAnniversary={daysToNext}
            userAvatar={currentUser?.avatar_emoji ?? '💕'}
            partnerAvatar={partner?.avatar_emoji ?? '💕'}
            userNickname={currentUser?.nickname ?? 'Me'}
            partnerNickname={partnerNickname}
            onSettings={() => navigate('/settings')}
          />
        )}
      </header>

      {/* Scrollable content */}
      <main className="flex-1 overflow-y-auto px-5 pb-[88px]">
        <div className="animate-fade-in-up flex flex-col gap-3">
          {/* Section: Recent notes */}
          <div className="[animation-delay:60ms] animate-fade-in-up">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-['DM_Sans'] text-[15px] font-medium text-[#2C1810]">
                Recent notes
              </h2>
              <button
                type="button"
                onClick={() => navigate('/notes')}
                className="font-['DM_Sans'] text-[13px] text-[#D4537E] active:opacity-60 transition-opacity duration-100 touch-manipulation"
              >
                See all
              </button>
            </div>

            {notesLoading ? (
              <div className="flex flex-col gap-2">
                <SkeletonCard />
                <SkeletonCard />
              </div>
            ) : recentNotes.length > 0 ? (
              <div className="flex flex-col gap-2">
                {recentNotes.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    onPress={() => navigate(`/notes/${note.id}`)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="✉️"
                title="No notes yet"
                subtitle={`Write your first love note and send it to ${partnerNickname}.`}
              />
            )}
          </div>
          {/* Section: Wishlist — recently added */}
          {recentWishlistItems.length > 0 && (
            <div className="mt-3 [animation-delay:90ms] animate-fade-in-up">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-['DM_Sans'] text-[15px] font-medium text-[#2C1810]">
                  Wishlist 🌟
                </h2>
                <button
                  type="button"
                  onClick={() => navigate('/wishlist')}
                  className="font-['DM_Sans'] text-[13px] text-[#D4537E] active:opacity-60 transition-opacity duration-100 touch-manipulation"
                >
                  See all →
                </button>
              </div>
              <div className="bg-white rounded-[16px] border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] overflow-hidden">
                {recentWishlistItems.map((item, idx) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate(`/wishlist/${item.vibe}`)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3
                      active:bg-[#FBEAF0] transition-colors duration-100 touch-manipulation text-left
                      ${idx < recentWishlistItems.length - 1 ? 'border-b border-[#F9E0E8]' : ''}
                    `}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-['DM_Sans'] text-[14px] text-[#2C1810] truncate">
                        {item.title}
                      </p>
                      <p className="font-['DM_Sans'] text-[12px] text-[#B89AA2] truncate mt-[1px]">
                        {item.isAddedByMe
                          ? `You · ${item.vibe}`
                          : `${partnerNickname} · ${item.vibe}`}
                      </p>
                    </div>
                    <span className="text-[#F4C0D1] text-[14px] flex-shrink-0">›</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Section: Upcoming countdown */}{' '}
          <div className="mt-3 [animation-delay:120ms] animate-fade-in-up">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-['DM_Sans'] text-[15px] font-medium text-[#2C1810]">
                Next countdown
              </h2>
              <button
                type="button"
                onClick={() => navigate('/countdowns')}
                className="font-['DM_Sans'] text-[13px] text-[#D4537E] active:opacity-60 transition-opacity duration-100 touch-manipulation"
              >
                See all
              </button>
            </div>

            {countdownsLoading ? (
              <SkeletonCard />
            ) : nextCountdown ? (
              <CountdownCard countdown={nextCountdown} onPress={() => navigate('/countdowns')} />
            ) : (
              <EmptyState
                icon="⏳"
                title="No countdowns yet"
                subtitle="Create a countdown to your next special moment."
              />
            )}
          </div>
          {/* Section: Milestones */}
          <div className="mt-3 [animation-delay:180ms] animate-fade-in-up">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-['DM_Sans'] text-[15px] font-medium text-[#2C1810]">
                Milestones
              </h2>
              <button
                type="button"
                onClick={() => navigate('/milestones/new')}
                className="font-['DM_Sans'] text-[13px] text-[#D4537E] active:opacity-60 transition-opacity duration-100 touch-manipulation"
              >
                Add
              </button>
            </div>
            <button
              type="button"
              onClick={() => navigate('/milestones')}
              className="w-full bg-white rounded-[16px] border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] p-4 text-center active:scale-[0.98] transition-all duration-150"
            >
              <span className="font-['DM_Sans'] text-[14px] text-[#D4537E]">View timeline →</span>
            </button>
          </div>
        </div>
      </main>

      <BottomNav />
    </div>
  )
}
